import { useState } from "react";
import { Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import { http, formatError } from "@/lib/api";
import { fmtDuration, uploadVideo } from "@/lib/video";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function handleError(e, setGuide, url) {
  const d = e.response?.data?.detail;
  if (e.response?.status === 402) { window.dispatchEvent(new CustomEvent("paywall", { detail: d })); return; }
  if (d?.code === "unsupported_site" || d?.code === "not_public") { setGuide({ url, message: d.message, notPublic: d.code === "not_public" }); return; }
  toast.error(typeof d === "string" ? d : d?.message || formatError(e));
}

// Prepares a video (upload or link), asks the student to confirm the message cost, then starts processing.
export function useVideoFlow({ ensureChat, onStarted }) {
  const [prep, setPrep] = useState(null);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(null);
  const [starting, setStarting] = useState(false);
  const [guide, setGuide] = useState(null);
  const fromFile = async (file) => {
    setProgress(0);
    setGuide(null);
    try { setPrep(await uploadVideo(file, setProgress)); setNote(""); } catch (e) { handleError(e, setGuide); } finally { setProgress(null); }
  };
  const fromLink = async (url, text) => {
    setProgress(-1);
    setGuide(null);
    try { setPrep((await http.post("/videos/link", { url })).data); setNote(text); return true; } catch (e) { handleError(e, setGuide, url); return ["unsupported_site", "not_public"].includes(e.response?.data?.detail?.code); } finally { setProgress(null); }
  };
  const start = async () => {
    setStarting(true);
    try {
      const chatId = await ensureChat();
      const { data } = await http.post("/videos/start", { prepare_id: prep.prepare_id, chat_id: chatId, note });
      setPrep(null);
      onStarted(chatId, data.user_message);
    } catch (e) { handleError(e, setGuide); } finally { setStarting(false); }
  };
  return { prep, setPrep, progress, fromFile, fromLink, start, starting, guide, setGuide };
}

export { DownloadGuide as VideoGuide } from "@/components/DownloadGuide";

export function VideoProgress({ progress }) {
  if (progress === null) return null;
  return (
    <div className="flex items-center gap-2 text-xs text-sky-200 mb-2" data-testid="video-upload-progress">
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {progress < 0 ? "Checking the video link…" : `Uploading video… ${progress}%`}
      {progress >= 0 && <span className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden"><span className="block h-full bg-sky-400 transition-[width]" style={{ width: `${progress}%` }} /></span>}
    </div>
  );
}

export function VideoConfirmDialog({ flow }) {
  const { prep, setPrep, start, starting } = flow;
  return (
    <Dialog open={!!prep} onOpenChange={(v) => !v && setPrep(null)}>
      <DialogContent className="bg-[#121725] border-white/10 text-white" data-testid="video-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Film className="h-4 w-4 text-sky-300" /> Learn from this video</DialogTitle>
          <DialogDescription className="text-neutral-400 truncate">{prep?.title}</DialogDescription>
        </DialogHeader>
        {prep?.thumbnail && <img src={prep.thumbnail} alt="" className="rounded-xl w-full aspect-video object-cover" />}
        <p className="text-sm text-neutral-300">Length {fmtDuration(prep?.duration)}</p>
        <p className="text-sm" data-testid="video-cost">This video will use <b>{prep?.cost} message{prep?.cost === 1 ? "" : "s"}</b>.</p>
        {prep && !prep.visual && <p className="text-xs text-neutral-500" data-testid="video-visual-note">On Free, BrynAI learns from what's said. Understanding slides and on-screen text comes with Starter.</p>}
        <button onClick={start} disabled={starting} data-testid="video-start-btn"
          className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-sky-400 text-white px-4 py-2 text-sm hover:bg-sky-500 disabled:opacity-50 transition-colors">
          {starting && <Loader2 className="h-4 w-4 animate-spin" />} Watch and explain
        </button>
      </DialogContent>
    </Dialog>
  );
}
